/* =========================================================================
   OhMy! Mini-Warenkorb — see snippets/ohmy-mini-cart.liquid.

   The visible cart is the Section Store cart app (app embed "block-cart"),
   not the absorb drawer. Everything here goes through the app's public API
   (window.SectionStoreCart v1): onBeforeClose cancels the close so the panel
   can shrink into the dock first, onBeforeOpen lets it grow back out,
   subscribe() keeps count and total in sync. The only thing read outside the
   API is the panel element (to animate it) and the reward tiers from the
   app's data-config. If either is missing the dock simply stays hidden or
   falls back to /cart.
   ========================================================================= */
(() => {
  'use strict';

  const root = document.getElementById('OmMiniCart');
  if (!root) return;

  const $  = (s, r = root) => r.querySelector(s);
  const $$ = (s, r = root) => Array.from(r.querySelectorAll(s));
  const mq = matchMedia('(max-width: 767px)');
  const rm = matchMedia('(prefers-reduced-motion: reduce)');
  const cssVar = (n, f) => getComputedStyle(document.documentElement).getPropertyValue(n).trim() || f;
  const SLATE = cssVar('--c-text', '#2F3E4E');
  const DUR = 420;
  const BUMP = root.dataset.bump === 'true';
  const T = root.dataset;

  let api = null;
  let count = +T.count || 0;
  let total = +T.total || 0;
  let busy = false, letClose = false, fromDock = false;
  let addPending = false, bumpOk = false, addTimer;

  /* ---------- money + reward tiers ---------- */
  function money(cents) {
    if (api) return api.formatMoney(cents);
    const fmt = (window.OhMy && window.OhMy.moneyFormat || '{{amount_with_comma_separator}} €').replace(/<[^>]*>/g, '');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      const comma = key.includes('comma');
      const [int, dec] = (cents / 100).toFixed(key.includes('no_decimals') ? 0 : 2).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
      return dec ? grouped + (comma ? ',' : '.') + dec : grouped;
    });
  }

  // Tiers the app actually rewards at checkout; theme settings only as fallback.
  let tiers = [
    { at: +T.ship || 0, type: 'shipping_discount' },
    { at: +T.gift || 0, type: 'free_gift' }
  ].filter((t) => t.at > 0);

  function readAppTiers(host) {
    try {
      const rb = JSON.parse(host.dataset.config).reward_bar;
      if (!rb || !rb.enabled || !Array.isArray(rb.tiers) || !rb.tiers.length) return;
      tiers = rb.tiers
        .map((t) => ({ at: Math.round(parseFloat(t.threshold) * 100), type: t.reward_type }))
        .filter((t) => t.at > 0)
        .sort((a, b) => a.at - b.at);
    } catch { /* keep the fallback */ }
  }

  function progress() {
    if (!tiers.length) return { pct: 0, done: false, text: '' };
    const last = tiers[tiers.length - 1].at;
    const pct = Math.min(100, (total / last) * 100);
    const next = tiers.find((t) => total < t.at);
    if (next) {
      const tpl = next.type === 'shipping_discount' ? T.tShip : T.tGift;
      return { pct, done: false, text: tpl.replace('%A%', money(next.at - total)) };
    }
    return { pct, done: true, text: tiers.length > 1 ? T.tDoneAll : T.tDoneShip };
  }

  function render() {
    const { pct, done, text } = progress();
    $$('[data-om-mc-count]').forEach((n) => { n.textContent = count; });
    $('[data-om-mc-ring]').style.strokeDashoffset = 100 - pct;
    $('[data-om-mc-line]').style.width = pct + '%';
    $('[data-om-mc-total]').textContent = money(total);
    $('[data-om-mc-text]').textContent = text;
    const peek = $('[data-om-mc-peek]');
    peek.textContent = '';
    const b = document.createElement('b');
    b.textContent = `${count} Artikel · ${money(total)}`;
    peek.append(b);
    if (text) { const s = document.createElement('small'); s.textContent = text; peek.append(s); }
    root.classList.toggle('is-done', done);
    const label = `Warenkorb öffnen, ${count} Artikel, ${money(total)}`;
    $$('[data-om-mc-open]').forEach((el) => el.setAttribute('aria-label', label));
  }

  /* ---------- dock visibility ---------- */
  const target = () => (mq.matches ? $('.om-mc__bar') : $('.om-mc__btn'));
  const hide = () => { root.hidden = true; };
  function show(invisible) {
    if (!count) return hide();
    root.hidden = false;
    root.style.opacity = invisible ? '0' : '';
  }
  function sync() {
    if (busy) return;
    if (!count || (api && api.isOpen())) return hide();
    if (addPending && !bumpOk) return;            // first add: the drawer opens, no dock flash
    show();
  }

  let peekT;
  function peek(ms) {
    root.classList.add('is-peek');
    clearTimeout(peekT);
    peekT = setTimeout(() => root.classList.remove('is-peek'), ms);
  }
  function nudge() {
    root.classList.remove('is-bump');
    void root.offsetWidth;
    root.classList.add('is-bump');
    peek(2200);
  }

  /* ---------- panel <-> dock ---------- */
  let host = null;
  const panel = () => host && host.shadowRoot && host.shadowRoot.querySelector('aside.cart-drawer');
  const overlay = () => host && host.shadowRoot && host.shadowRoot.querySelector('.cart-overlay');

  function toDock(fromRect) {
    const k = target().getBoundingClientRect();
    return `translate(${k.left - fromRect.left}px, ${k.top - fromRect.top}px) scale(${k.width / fromRect.width}, ${k.height / fromRect.height})`;
  }
  function freeze(p) { p.style.transition = 'none'; p.style.transformOrigin = '0 0'; }
  function thaw(p) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      p.style.transition = '';
      p.style.transformOrigin = '';
      p.style.opacity = '';
    }));
  }
  const fadeKids = (p, frames, opts) => Array.from(p.children).map((c) => c.animate(frames, opts));
  const onceClosed = (ms) => new Promise((res) => {
    const t = setTimeout(res, ms);
    window.addEventListener('section-store:cart:closed', () => { clearTimeout(t); res(); }, { once: true });
  });

  async function minimize() {
    busy = true;
    const p = panel();
    show(true);                                   // lay out invisibly so it can be measured
    const anims = [];

    if (p && !rm.matches) {
      const from = p.getBoundingClientRect();
      const to = toDock(from);
      freeze(p);
      anims.push(...fadeKids(p, [{ opacity: 1 }, { opacity: 0 }], { duration: 120, fill: 'forwards' }));
      const ov = overlay();
      if (ov && !ov.hidden) anims.push(ov.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, fill: 'forwards' }));
      const a = p.animate([
        { transform: 'none', borderRadius: '0px', backgroundColor: getComputedStyle(p).backgroundColor },
        { transform: to, borderRadius: '16px', backgroundColor: SLATE, offset: 0.85 },
        { transform: to, borderRadius: '16px', backgroundColor: SLATE }
      ], { duration: DUR, easing: 'cubic-bezier(.55,.05,.35,1)', fill: 'forwards' });
      anims.push(a);
      await a.finished.catch(() => {});
    }

    const closed = onceClosed(600);
    letClose = true;
    api.close();
    letClose = false;
    await closed;                                 // keep the shrunken frame until the app has hidden the panel
    anims.forEach((x) => x.cancel());
    if (p) thaw(p);

    root.style.opacity = '';
    if (!rm.matches) {
      target().animate([{ transform: 'scale(.85)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }], { duration: 300, easing: 'ease-out' });
    }
    busy = false;
    sync();
    peek(2600);
    target().focus({ preventScroll: true });
  }

  function growFromDock() {
    const p = panel();
    if (!p || rm.matches || root.hidden) return;
    const k = target().getBoundingClientRect();
    freeze(p);
    p.style.opacity = '0';                        // one invisible frame while the app lays it out
    requestAnimationFrame(() => {
      const r = p.getBoundingClientRect();
      p.style.opacity = '';
      const from = `translate(${k.left - r.left}px, ${k.top - r.top}px) scale(${k.width / r.width}, ${k.height / r.height})`;
      fadeKids(p, [{ opacity: 0 }, { opacity: 0, offset: 0.35 }, { opacity: 1 }], { duration: DUR });
      p.animate([
        { transform: from, borderRadius: '16px', backgroundColor: SLATE },
        { backgroundColor: getComputedStyle(p).backgroundColor, offset: 0.3 },
        { transform: 'none', borderRadius: '0px' }
      ], { duration: DUR, easing: 'cubic-bezier(.22,.8,.24,1)' }).finished.catch(() => {}).then(() => thaw(p));
    });
  }

  /* ---------- wire up to the app ---------- */
  function boot(a) {
    if (api) return;
    api = a;
    host = document.querySelector('section-store--cart-block');
    if (host) readAppTiers(host);

    api.subscribe((cart) => {
      if (!cart) return;
      count = cart.item_count;
      total = cart.total_price;
      render();
      sync();
    });

    api.onBeforeClose(() => {
      if (letClose || busy || !count || !panel()) return true;
      minimize();
      return false;
    });

    api.onBeforeAddToCart(() => {
      addPending = true;
      bumpOk = BUMP && !root.hidden;
      clearTimeout(addTimer);
      addTimer = setTimeout(() => { addPending = false; sync(); }, 6000);
      return true;
    });
    window.addEventListener('section-store:cart:error', () => { addPending = false; sync(); });

    api.onBeforeOpen(() => {
      if (busy) return false;
      if (addPending) {
        addPending = false;
        clearTimeout(addTimer);
        if (bumpOk) { nudge(); return false; }
      }
      if (fromDock) { fromDock = false; growFromDock(); }
      hide();
      return true;
    });

    window.addEventListener('section-store:cart:opened', hide);
    window.addEventListener('section-store:cart:closed', () => sync());
    render();
    sync();
  }

  root.addEventListener('click', (e) => {
    if (!e.target.closest('[data-om-mc-open]') || busy) return;
    if (!api) { window.location.href = (window.Shopify?.routes?.root || '/') + 'cart'; return; }
    fromDock = true;
    api.open();
    fromDock = false;
  });

  if (window.SectionStoreCart) window.SectionStoreCart.ready.then(() => boot(window.SectionStoreCart));
  else window.addEventListener('section-store:cart:ready', () => window.SectionStoreCart && boot(window.SectionStoreCart), { once: true });

  /* ---------- sit above bottom-pinned buy bars (#OmSticky, PDP sticky bar on mobile) ---------- */
  const stickies = Array.from(document.querySelectorAll('#OmSticky, [data-sticky-bar]'));
  function lift() {
    let h = 0;
    stickies.forEach((el) => {
      if (el.classList.contains('is-visible') && getComputedStyle(el).bottom === '0px') h = Math.max(h, el.offsetHeight);
    });
    root.style.setProperty('--om-mc-lift', h + 'px');
  }
  const mo = new MutationObserver(lift);
  stickies.forEach((el) => mo.observe(el, { attributes: true, attributeFilter: ['class'] }));
  mq.addEventListener('change', lift);
  lift();

  render();
})();
